import { paramsToQuery } from '@/base/MaUtils.js'
import { makeRequest } from '@/base/MaRequestPooler.js'

/**
 * Returns CPP list.
 * @param trackId
 * @param countryCode
 * @param device
 * @param startDate
 * @param endDate
 * @returns {Promise}
 */
export function getCustomProductPages({ trackId, countryCode, device, startDate, endDate }) {
  return makeRequest(`/account/store-account/app/${trackId}/cpp-summary?countryCode=${countryCode}&device=${device}&startDate=${startDate}&endDate=${endDate}`)
}

/**
 * Returns CPP daily stats for cpp.
 * @param trackId
 * @param countryCode
 * @param device
 * @param startDate
 * @param endDate
 * @param metric
 * @param cppId
 * @returns {Promise}
 */
export function getCPPDailyStats({ trackId, countryCode, device, startDate, endDate, cppId }) {
  return makeRequest(`/account/store-account/app/${trackId}/daily-stats-for-cpp?countryCode=${countryCode}&device=${device}&startDate=${startDate}&endDate=${endDate}&cppId=${cppId}`)
}

/**
 * Returns CPP By App list.
 * @param trackId
 * @param countryCode
 * @param startDate
 * @param endDate
 * @returns {Promise}
 */
export function getCPPByApp({ trackId, countryCode, startDate, endDate }) {
  return makeRequest(`/sa/stats/cpp/${trackId}/${countryCode}?startDate=${startDate}&endDate=${endDate}`)
}

/**
 * Returns CPP Keywords.
 * @param {object} data - The parameters for fetching CPP data.
 * @param {string} data.trackId - The unique identifier for the app.
 * @param {string} data.countryCode - The ISO country code.
 * @param {string} data.startDate - The start date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} data.endDate - The end date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} data.keywordFilter - The search term to fetch data for.
 * @param {string} data.cppId - The CPP ID to fetch data for.
 * @param {string} data.pageSize - The number of records per page.
 * @param {string} data.currentPage - The current page number for pagination.
 * @returns {Promise<Array>} A promise that resolves to the CPP data.
 */
export function getCPPKeywords({ trackId, countryCode, startDate, endDate, keywordFilter, cppId, pageSize, currentPage }) {
  return makeRequest(`/sa/stats/cpp-keywords/${trackId}/${countryCode}?${paramsToQuery({ cppId, keywordFilter, startDate, endDate, pageSize, currentPage })}`)
}

/**
 * Returns CPP Keywords CSV data.
 * @param {object} data - The parameters for fetching CPP data.
 * @param {string} data.trackId - The unique identifier for the app.
 * @param {string} data.countryCode - The ISO country code.
 * @param {string} data.startDate - The start date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} data.endDate - The end date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} data.keywordFilter - The search term to fetch data for.
 * @param {string} data.cppId - The CPP ID to fetch data for.
 * @param {string} data.pageSize - The number of records per page.
 * @param {string} data.currentPage - The current page number for pagination.
 * @returns {Promise<Array>} A promise that resolves to the CPP data.
 */
export function getCPPKeywordsCsvData({ trackId, countryCode, startDate, endDate, keywordFilter, cppId, pageSize, currentPage }) {
  return makeRequest(`/sa/stats/cpp-keywords/export/${trackId}/${countryCode}?${paramsToQuery({ cppId, keywordFilter, startDate, endDate, pageSize, currentPage })}`)
}

/**
 * Returns CPP By Keyword.
 * @param trackId
 * @param countryCode
 * @param startDate
 * @param endDate
 * @returns {Promise}
 */
export function getCPPByKeyword({ keyword, countryCode, startDate, endDate }) {
  return makeRequest(`/sa/stats/keyword-cpp/${countryCode}?keyword=${keyword}&startDate=${startDate}&endDate=${endDate}`)
}

/**
 * Returns App CPP available countries.
 * @param trackId
 * @param startDate
 * @param endDate
 * @returns {Promise}
 */
export function getAppCppCountries({ trackId, startDate, endDate }) {
  return makeRequest(`/sa/stats/cpp/${trackId}/countries?${paramsToQuery({
    startDate,
    endDate,
  })}`)
}

/**
 * Returns CPP By Category.
 * @param {object} data - The parameters for fetching CPP data.
 * @param {string} data.countryCode - The ISO country code.
 * @param {string} data.startDate - The start date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} data.endDate - The end date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {number} data.categoryId - The category ID to fetch data for.
 * @param {number} data.currentPage - The current page number for pagination.
 * @param {number} data.pageSize - The number of records per page.
 * @param {string} data.sortColumn - The column name to sort the data by.
 * @param {string} data.sortOrder - The sorting order ('ASC' or 'DESC').
 * @returns {Promise<Array>} A promise that resolves to the CPP data.
 */
export function getCPPByCategory({ countryCode, categoryId, startDate, endDate, currentPage, pageSize, sortColumn, sortOrder, isTaxonomy, subGenreIdList }) {
  return makeRequest(`/sa/stats/category-cpp/${countryCode}?${paramsToQuery({
    startDate,
    endDate,
    category: categoryId,
    currentPage,
    pageSize,
    sortColumn,
    order: sortOrder,
    isTaxonomy,
    subGenreIdList,
  })}`)
}

/**
 * Fetches the Cost Per Placement (CPP) data by ad networks.
 * This function sends a POST request to the specified endpoint with the given parameters.
 *
 * @param {object} params - The parameters for fetching CPP data.
 * @param {string} params.startDate - The start date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} params.endDate - The end date for the data range, formatted as 'YYYY-MM-DD'.
 * @param {string} params.trackId - The unique identifier for the app.
 * @param {string} [params.network] - The ad network's name. Optional.
 * @param {string} [params.category] - The category of the app. Optional.
 * @param {string} params.countryCode - The ISO country code.
 * @param {string} [params.creativeType] - The type of creative  Optional.
 * @param {number} [params.currentPage] - The current page number for pagination. Optional.
 * @param {number} [params.page] - Alias for 'currentPage'. Optional.
 * @param {number} [params.length] - The number of records per page. Optional.
 * @param {string} [params.sortColumn] - The column name to sort the data by. Optional.
 * @param {string} [params.order] - The sorting order ('asc' or 'desc'). Optional.
 * @param {boolean} [params.isTaxonomy] - Whether the category is a taxonomy. Optional.
 * @param {Array} [params.taxonomies] - The list of taxonomy IDs. Optional.
 * @returns {Promise<object>} A promise that resolves to the CPP data.
 */
export function getCPPAdNetworks(data) {
  return makeRequest(`/ad-intelligence/cpp-intelligence/creatives/list`, {
    method: 'POST',
    data,
  })
}

/**
 * Fetches the CPP creatives by ad networks.
 *
 * @param {Array} cppIdList - The list of CPP IDs.
 * @returns {Promise<object>} A promise that resolves to the CPP data.
 */
export function getCPPAdNetworkCreatives(cppIdList) {
  return makeRequest(`/cpp/list`, {
    method: 'POST',
    data: cppIdList,
  })
}
